import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Layers, Timer, ArrowRight, Sparkles, ClipboardCheck } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common/EmptyState";
import { learningApi } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";

const MODE_META = {
    study: { icon: Layers, accent: "hsl(var(--primary))", to: (id) => `/study/${id}` },
    exam: { icon: Timer, accent: "hsl(38,92%,58%)", to: (id) => `/exam/${id}` },
};

export const DocPickerGrid = ({ mode = "study" }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const meta = MODE_META[mode] || MODE_META.study;
    const [docs, setDocs] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const data = await learningApi.overview();
                setDocs(data?.per_document || []);
            } catch (e) {
                setDocs([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="docpicker-loading">
                {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32 rounded-2xl bg-surface-2" />)}
            </div>
        );
    }

    if (!docs || docs.length === 0) {
        return (
            <EmptyState
                testid="docpicker-empty"
                icon={meta.icon}
                title={t(`docpicker.${mode}.emptyTitle`)}
                body={t(`docpicker.${mode}.emptyBody`)}
                cta={t("docpicker.goToLibrary")}
                ctaIcon={Sparkles}
                onCta={() => navigate("/library")}
            />
        );
    }

    return (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="docpicker-grid">
            {docs.map((d, i) => (
                <motion.button
                    key={d.id}
                    data-testid="docpicker-item"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: Math.min(i * 0.04, 0.3), ease: [0.22, 1, 0.36, 1] }}
                    onClick={() => navigate(meta.to(d.id))}
                    className="group flex flex-col rounded-2xl border border-border bg-surface-1 p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-[hsl(var(--primary))]/40 hover:bg-surface-2"
                >
                    <div className="flex items-start justify-between gap-3">
                        <h3 className="line-clamp-2 font-sans-display text-sm font-semibold text-t1">{d.title}</h3>
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--primary))]/15" style={{ color: meta.accent }}>
                            <meta.icon className="h-4 w-4" />
                        </div>
                    </div>
                    <div className="mt-2 flex items-center gap-3 mono-label text-t4">
                        <span className="inline-flex items-center gap-1"><Layers className="h-3 w-3" /> {d.cards} {t("docpicker.cards")}</span>
                        <span className="inline-flex items-center gap-1"><ClipboardCheck className="h-3 w-3" /> {d.quizzes} {t("docpicker.quizzes")}</span>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-3">
                            <div className="h-full rounded-full bg-[hsl(var(--primary))] transition-all duration-500" style={{ width: `${d.scores?.mastery || 0}%` }} />
                        </div>
                        <span className="w-8 text-right font-sans-display text-xs font-semibold text-t1">{d.scores?.mastery || 0}</span>
                    </div>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--primary))] opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        {mode === "exam" ? t("docpicker.ctaExam") : t("docpicker.ctaStudy")} <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                </motion.button>
            ))}
        </div>
    );
};
